import moment from "moment";
import { CallAPIPromise } from "../../billing/common";

const text = (value) => String(value ?? "").trim();
const amount = (value) => Number(value || 0);
const round = (value) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

const firstValue = (row, names) => {
  for (const name of names) {
    if (row?.[name] !== undefined && row?.[name] !== null && text(row[name])) return row[name];
  }
  return "";
};

const required = (value, label) => {
  const result = text(value);
  if (!result) throw new Error(`${label} is required.`);
  return result;
};

const validPin = (value, label) => {
  const pin = Number(value);
  if (!Number.isInteger(pin) || pin < 100000 || pin > 999999) {
    throw new Error(`${label} must be a valid 6-digit PIN code.`);
  }
  return pin;
};

const buyerGstin = (value) => {
  const gstin = required(value, "Buyer GSTIN").toUpperCase();
  if (!/^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/.test(gstin)) {
    throw new Error("Buyer GSTIN is invalid. Correct it in Account Master.");
  }
  return gstin;
};

const phoneDigits = (value) => text(value).replace(/\D/g, "").slice(-10);

const irpUnit = (value) => {
  const unit = text(value).toUpperCase();
  const map = {
    GRM: "GMS", GRAM: "GMS", GRAMS: "GMS", GM: "GMS",
    KG: "KGS", KILOGRAM: "KGS", KILOGRAMS: "KGS",
    PC: "NOS", PCS: "NOS", PIECE: "NOS", PIECES: "NOS",
    LITER: "LTR", LITRE: "LTR", LT: "LTR",
    ML: "MLT", MILLILITER: "MLT", MILLILITRE: "MLT",
  };
  return map[unit] || unit.slice(0, 8) || "NOS";
};

async function loadAccount(bill, tablePrefix, token) {
  const accountNo = text(bill.ACCOUNT_NO);
  if (!accountNo || Number(accountNo) === 0) return null;
  const accountFields = ["NAME", "ADDRESS", "MOBILE", "EMAIL", "TAX_NUMBER", "PIN_CODE", "CITY", "STATE", "COUNTRY"];
  if (accountFields.every((field) => text(bill[field]))) return null;

  const response = await CallAPIPromise(
    "/api/accountMaster/listAccountMaster",
    {
      table_prefix: tablePrefix,
      SECTION_NAME: "",
      mobile: "",
      name: "",
      account_no: accountNo,
      page: 1,
      limit: 100,
    },
    token,
  );

  const accounts = response?.data?.response?.data || [];
  return accounts.find((account) => text(account.ACCOUNT_NO) === accountNo) || null;
}

function mergeAccount(rows, account) {
  if (!account) return rows;
  const fields = [
    "ACCOUNT_NO", "NAME", "ADDRESS", "MOBILE", "EMAIL", "TAX_NUMBER",
    "PIN_CODE", "CITY", "STATE", "COUNTRY",
  ];
  return rows.map((row) => {
    const merged = { ...row, _EINVOICE_PARTY_SOURCE: "ACCOUNT_MASTER" };
    fields.forEach((field) => {
      if (!text(merged[field]) && text(account[field])) merged[field] = account[field];
    });
    return merged;
  });
}

async function refreshItemsFromMaster(rows, tablePrefix, token) {
  return Promise.all(rows.map(async (row) => {
    const itemCode = text(firstValue(row, ["ICODE", "ITEM_CODE", "PRODUCT_CODE"]));
    if (!itemCode) return row;
    try {
      const response = await CallAPIPromise(
        "/api/itemMasterUtils/listItemMaster",
        {
          table_prefix: tablePrefix,
          ICODE: itemCode,
          ITEMNAME: "",
          ALIAS_CODE: "",
          SECTION_NAME: "",
          page: 1,
          PAGE: 1,
          limit: 10,
        },
        token,
      );
      const items = response?.data?.response?.data || [];
      const masterItem = items.find((item) => text(item.ICODE) === itemCode) || (items.length === 1 ? items[0] : null);
      if (!masterItem) return row;
      return {
        ...row,
        HSN_CODE: text(masterItem.HSN_CODE) || row.HSN_CODE,
        UNIT: text(masterItem.UNIT || masterItem.UQC) || row.UNIT,
      };
    } catch {
      return row;
    }
  }));
}

export async function getEInvoicePayload({ invoiceNo, tablePrefix, companyDetails, token }) {
  const billResponse = await CallAPIPromise(
    "/api/listBillsRoute/loadBill",
    {
      table_prefix: tablePrefix,
      INVNO: invoiceNo,
      BILL_PERFORMA: "bills",
    },
    token,
  );

  const billRows = billResponse?.data?.response;
  if (!Array.isArray(billRows) || !billRows.length) throw new Error("RetailX bill was not found.");
  if (text(billRows[0].BILL_STATUS).toLowerCase() === "void") throw new Error("Void bill cannot create an e-invoice.");

  const accountNo = text(billRows[0].ACCOUNT_NO);
  if (!accountNo || Number(accountNo) === 0) {
    throw new Error("Select an Account Master party on this bill before generating an e-invoice.");
  }
  let account = null;
  try {
    account = await loadAccount(billRows[0], tablePrefix, token);
  } catch (accountError) {
    const requiredSnapshot = ["NAME", "ADDRESS", "TAX_NUMBER", "PIN_CODE", "CITY"];
    if (!requiredSnapshot.every((field) => text(billRows[0][field]))) {
      throw new Error("Account Master details could not be loaded for this bill.");
    }
  }
  const accountRows = mergeAccount(billRows, account);
  const rows = await refreshItemsFromMaster(accountRows, tablePrefix, token);
  const bill = rows[0];
  const documentNumber = required(bill.INVNO, "Invoice number");
  if (documentNumber.length > 16 || !/^[A-Za-z0-9/-]+$/.test(documentNumber)) {
    throw new Error("Invoice number must be 1–16 characters and use only letters, numbers, / or -.");
  }
  const documentDate = moment(bill.DATE);
  if (!documentDate.isValid()) throw new Error("Invoice date is invalid.");
  const gstin = buyerGstin(bill.TAX_NUMBER);
  const sellerGstin = required(
    process.env.REACT_APP_EINVOICE_TEST_GSTIN || companyDetails?.regdNo || companyDetails?.GSTIN,
    "Seller GSTIN",
  ).toUpperCase();
  const isInterState = sellerGstin.slice(0, 2) !== gstin.slice(0, 2);

  let assessableValue = 0;
  let cgstValue = 0;
  let sgstValue = 0;
  let igstValue = 0;

  const itemList = rows.map((item, index) => {
    const quantity = amount(firstValue(item, ["QNTY", "QTY", "QUANTITY"]));
    const unitPrice = amount(firstValue(item, ["SALE_PRICE", "RATE", "PRICE"]));
    const totalAmount = amount(firstValue(item, ["TOTAL", "AMOUNT"])) || round(quantity * unitPrice);
    const discount = amount(firstValue(item, ["DISQ_AMT", "DISCOUNT_AMOUNT"]));
    const assessable = amount(firstValue(item, ["AMOUNT", "TAXABLE_AMOUNT"])) || round(totalAmount - discount);
    const tax1Rate = amount(item.TAX1_RATE);
    const tax2Rate = amount(item.TAX2_RATE);
    const tax3Rate = amount(item.TAX3_RATE);
    const tax1Amount = amount(item.TAX1_AMT);
    const tax2Amount = amount(item.TAX2_AMT);
    const tax3Amount = amount(item.TAX3_AMT);
    const cgst = isInterState ? 0 : tax1Amount;
    const sgst = isInterState ? 0 : tax2Amount;
    const igst = isInterState ? (tax3Amount || round(tax1Amount + tax2Amount)) : 0;
    const gstRate = isInterState ? (tax3Rate || round(tax1Rate + tax2Rate)) : round(tax1Rate + tax2Rate);
    const hsn = text(firstValue(item, ["HSN_CODE", "HSN"])).replace(/\D/g, "");
    const itemName = text(firstValue(item, ["DESCRIPTION", "ITEMNAME", "ITEM_NAME", "PRODUCT_NAME", "PNAME"])) || `Item ${index + 1}`;
    if (quantity <= 0) throw new Error(`Item ${index + 1}: quantity must be greater than zero.`);
    if (hsn.length < 6 || hsn.length > 8) {
      throw new Error(`${itemName}: minimum 6-digit HSN code is required. Correct HSN ${hsn || "missing"} in Item Master.`);
    }

    assessableValue += assessable;
    cgstValue += cgst;
    sgstValue += sgst;
    igstValue += igst;

    return {
      SlNo: String(index + 1),
      PrdDesc: itemName.slice(0, 300),
      IsServc: "N",
      HsnCd: hsn,
      Qty: round(quantity),
      Unit: irpUnit(firstValue(item, ["UNIT", "UQC"])),
      UnitPrice: round(unitPrice),
      TotAmt: round(totalAmount),
      Discount: round(discount),
      AssAmt: round(assessable),
      GstRt: round(gstRate),
      CgstAmt: round(cgst),
      SgstAmt: round(sgst),
      IgstAmt: round(igst),
      TotItemVal: round(assessable + cgst + sgst + igst),
    };
  });

  const otherCharges = round(amount(bill.HANDLING_CHARGES) + amount(bill.DELIVERY_CHARGES));
  const discount = round(amount(bill.DISQ_AMT) + amount(bill.LOYALTY_DISCOUNT));
  const roundOff = round(amount(bill.ROUND_OFF));
  const calculatedTotal = round(assessableValue + cgstValue + sgstValue + igstValue + otherCharges - discount + roundOff);
  const usingTestSeller = Boolean(process.env.REACT_APP_EINVOICE_TEST_GSTIN);
  const sellerAddress = usingTestSeller ? process.env.REACT_APP_EINVOICE_TEST_SELLER_ADDRESS : companyDetails?.address;
  const sellerCity = usingTestSeller ? process.env.REACT_APP_EINVOICE_TEST_SELLER_CITY : (companyDetails?.city || companyDetails?.location);
  const sellerPin = usingTestSeller ? process.env.REACT_APP_EINVOICE_TEST_SELLER_PIN : (companyDetails?.pincode || companyDetails?.pinCode);

  return {
    Version: "1.1",
    TranDtls: {
      TaxSch: "GST",
      SupTyp: "B2B",
    },
    DocDtls: {
      Typ: "INV",
      No: documentNumber,
      Dt: documentDate.format("DD/MM/YYYY"),
    },
    SellerDtls: {
      Gstin: sellerGstin,
      LglNm: required(companyDetails?.companyName || companyDetails?.company_name, "Seller legal name").slice(0, 100),
      TrdNm: text(companyDetails?.companyName || companyDetails?.company_name).slice(0, 100) || undefined,
      Addr1: required(sellerAddress, "Seller address").slice(0, 100),
      Loc: required(sellerCity, "Seller city").slice(0, 50),
      Pin: validPin(sellerPin, "Seller PIN code"),
      Stcd: sellerGstin.slice(0, 2),
      Ph: phoneDigits(companyDetails?.mobile) || undefined,
      Em: text(companyDetails?.email || companyDetails?.company_email) || undefined,
    },
    BuyerDtls: {
      Gstin: gstin,
      LglNm: required(bill.NAME, "Buyer legal name").slice(0, 100),
      Pos: gstin.slice(0, 2),
      Addr1: required(bill.ADDRESS, "Buyer address").slice(0, 100),
      Loc: required(bill.CITY, "Buyer city").slice(0, 50),
      Pin: validPin(bill.PIN_CODE, "Buyer PIN code"),
      Stcd: gstin.slice(0, 2),
      Ph: phoneDigits(bill.MOBILE) || undefined,
      Em: text(bill.EMAIL) || undefined,
    },
    ItemList: itemList,
    ValDtls: {
      AssVal: round(assessableValue),
      CgstVal: round(cgstValue),
      SgstVal: round(sgstValue),
      IgstVal: round(igstValue),
      CesVal: 0,
      StCesVal: 0,
      Discount: discount,
      OthChrg: otherCharges,
      RndOffAmt: roundOff,
      TotInvVal: calculatedTotal,
    },
  };
}
